import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import type { AuthState } from '../../types';
import { apiService } from '../../services/api';
import Footer from './Footer';
import Logo from '../ui/Logo';
import {
  ChevronDownIcon,
  SettingsIcon,
  LogoutIcon,
  SpinnerIcon,
  MenuIcon,
  CloseIcon,
  HomeIcon,
  HoldingsIcon,
  TrendingUpIcon
} from '../ui/Icons';

interface LayoutProps {
  children: ReactNode;
  authState: AuthState;
  setAuthState: (authState: AuthState) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, authState, setAuthState }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      // Call logout API to invalidate server-side session
      await apiService.auth.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear local storage and update auth state
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      // Check if current path requires authentication
      const protectedPaths = [
        '/dashboard',
        '/settings',
        '/manage',
        '/admin',
        '/holdings',
        '/history',
        '/grow/signals',
        '/grow/market-action'
      ];

      const requiresAuth = protectedPaths.some(path => location.pathname.startsWith(path));

      if (requiresAuth) {
        // Navigate to home page if on a protected route
        navigate('/');
      }

      setIsLoggingOut(false);
    }
  };

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col">
      {/* Navigation Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Logo size="md" />
            </div>

            {/* Desktop Navigation */}
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {/* Manage Section */}
              <div className="relative group">
                <button className={`flex items-center space-x-1 text-sm font-medium transition-colors py-2 ${isActiveRoute('/dashboard') || isActiveRoute('/holdings') || isActiveRoute('/manage/alerts')
                  ? 'text-blue-700'
                  : 'text-slate-600 hover:text-blue-700'
                  }`}>
                  <span>Manage</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
                <div className="absolute left-0 mt-0 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
                  <Link to="/dashboard" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">Overview</Link>
                  <Link to="/holdings" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">Holdings</Link>
                  {/* <Link to="/history" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">History</Link> */}
                  <div className="border-t border-slate-100 my-1"></div>
                  <Link to="/manage/alerts" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">Price Alerts</Link>
                  {/* <Link to="/manage/news" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700">News & Insights</Link> */}
                </div>
              </div>

              {/* Grow Section */}
              <div className="relative group">
                <button className={`flex items-center space-x-1 text-sm font-medium transition-colors py-2 ${isActiveRoute('/grow') || isActiveRoute('/grow/unlisted') || isActiveRoute('/grow/murmurs')
                  ? 'text-trade-primary'
                  : 'text-slate-600 hover:text-trade-primary'
                  }`}>
                  <span>Grow</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>
                <div className="absolute left-0 mt-0 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
                  <Link to="/grow" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-trade-primary">Overview</Link>
                  <Link to="/grow/murmurs" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-trade-primary">Murmurs</Link>
                  {/* <Link to="/grow/unlisted" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-trade-primary">Unlisted Stocks</Link> */}
                  {/* <Link to="/grow/signals" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-trade-primary">Trade Signals</Link> */}
                  {/* <Link to="/grow/market-action" className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-trade-primary">Market Action</Link> */}
                </div>
              </div>

              {/* Admin Section */}
              {(authState.user?.isGrowthAdmin || authState.user?.isUnlistedAdmin) && (
                <div className="relative group">
                  <button className={`flex items-center space-x-1 text-sm font-medium transition-colors py-2 ${isActiveRoute('/admin/growth') || isActiveRoute('/admin/unlisted-stocks')
                    ? 'text-purple-700'
                    : 'text-slate-600 hover:text-purple-700'
                    }`}>
                    <SettingsIcon className="w-4 h-4" />
                    <span>Admin</span>
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                  <div className="absolute left-0 mt-0 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 z-50">
                    {authState.user?.isGrowthAdmin && (
                      <Link to="/admin/growth" className="block px-4 py-2 text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700">Growth</Link>
                    )}
                    {/* {(authState.user?.isGrowthAdmin || authState.user?.isUnlistedAdmin) && (
                      <Link to="/admin/unlisted-stocks" className="block px-4 py-2 text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700">Unlisted Stocks</Link>
                    )} */}
                  </div>
                </div>
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              {/* User Info - Hidden on mobile */}
              {authState.user && (
                <div className="hidden lg:flex items-center space-x-3">
                  <div className="flex items-center space-x-2 px-3 py-2 bg-slate-100 rounded-lg">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {authState.user.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {authState.user.name}
                    </span>
                  </div>
                </div>
              )}

              {/* Settings Button */}
              <Link
                to="/settings"
                className="p-2.5 text-slate-600 hover:text-blue-700 hover:bg-slate-100 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                title="Settings"
                aria-label="Account settings"
              >
                <SettingsIcon className="w-5 h-5" />
              </Link>

              {/* Auth Button */}
              {authState.isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                >
                  {isLoggingOut ? (
                    <>
                      <SpinnerIcon className="animate-spin w-4 h-4" />
                      <span>Signing out...</span>
                    </>
                  ) : (
                    <>
                      <LogoutIcon className="w-4 h-4" />
                      <span>Logout</span>
                    </>
                  )}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-trade-primary to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-md"
                >
                  <LogoutIcon className="w-4 h-4 rotate-180" />
                  <span>Login</span>
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="md:hidden p-2 text-slate-600 hover:text-blue-700 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Toggle mobile menu"
              >
                {showMobileMenu ? (
                  <CloseIcon className="w-6 h-6" />
                ) : (
                  <MenuIcon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
            <div className="px-4 py-3 space-y-1">
              <Link
                to="/dashboard"
                onClick={() => setShowMobileMenu(false)}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActiveRoute('/dashboard')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-blue-700 hover:bg-slate-100'
                  }`}
              >
                <HomeIcon className="w-5 h-5" />
                <span>Home</span>
              </Link>
              <Link
                to="/holdings"
                onClick={() => setShowMobileMenu(false)}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActiveRoute('/holdings')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-blue-700 hover:bg-slate-100'
                  }`}
              >
                <HoldingsIcon className="w-5 h-5" />
                <span>Holdings</span>
              </Link>
              {/* <Link
                to="/history"
                onClick={() => setShowMobileMenu(false)}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActiveRoute('/history')
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-blue-700 hover:bg-slate-100'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>History</span>
              </Link> */}
              {/* Admin Links */}
              {authState.user?.isGrowthAdmin && (
                <Link
                  to="/admin/growth"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActiveRoute('/admin/growth')
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-slate-600 hover:text-purple-700 hover:bg-slate-100'
                    }`}
                >
                  <TrendingUpIcon className="w-5 h-5" />
                  <span>Growth Admin</span>
                </Link>
              )}
              {/* {(authState.user?.isGrowthAdmin || authState.user?.isUnlistedAdmin) && (
                <Link
                  to="/admin/unlisted-stocks"
                  onClick={() => setShowMobileMenu(false)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActiveRoute('/admin/unlisted-stocks')
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-slate-600 hover:text-purple-700 hover:bg-slate-100'
                    }`}
                >
                  <SettingsIcon className="w-5 h-5" />
                  <span>Unlisted Admin</span>
                </Link>
              )} */}

              {/* Mobile user info and logout */}
              {authState.user && (
                <div className="pt-2 mt-2 border-t border-slate-200">
                  <div className="flex items-center space-x-3 px-3 py-2 text-sm text-slate-600">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {authState.user.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <span className="font-medium">{authState.user.name}</span>
                  </div>
                  <button
                    onClick={() => {
                      setShowMobileMenu(false);
                      handleLogout();
                    }}
                    disabled={isLoggingOut}
                    className="w-full flex items-center space-x-3 px-3 py-2 mt-1 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>{isLoggingOut ? 'Signing out...' : 'Logout'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer authState={authState} />
    </div>
  );
};

export default Layout; 